import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import CollateralPanel from '../components/CollateralPanel.jsx';
import EmiPlanSelector from '../components/EmiPlanSelector.jsx';
import ProceedModal from '../components/ProceedModal.jsx';
import ScheduleModal from '../components/ScheduleModal.jsx';
import VariantSelector from '../components/VariantSelector.jsx';
import { Badge, Button, ErrorState, ProductImage, Rating, Skeleton } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { currency, cx, percent } from '../lib/format.js';
import { useAsync } from '../lib/useAsync.js';

/**
 * ProductDetail — the core page of the assignment.
 *
 * Reached via a unique, shareable URL (/products/:slug). All product, variant and
 * EMI data comes from the API; nothing on this page is hardcoded.
 *
 * Selection state lives here because variant and plan are interdependent: each
 * variant carries its own EMI ladder, so changing the variant must re-resolve the
 * chosen plan. The rule applied below is to preserve the shopper's *tenure*
 * preference across a variant change rather than resetting to the default, since
 * someone comparing capacities at 12 months wants to stay at 12 months.
 */

function Breadcrumbs({ product }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm">
      <ol className="flex flex-wrap items-center gap-1.5 text-ink-500">
        <li>
          <Link to="/" className="hover:text-brand-700">
            Shop
          </Link>
        </li>
        <li aria-hidden="true">/</li>
        {product.brand && (
          <>
            <li>{product.brand}</li>
            <li aria-hidden="true">/</li>
          </>
        )}
        <li className="font-medium text-ink-800" aria-current="page">
          {product.name}
        </li>
      </ol>
    </nav>
  );
}

function Gallery({ product, variant }) {
  // Prefer the selected variant's imagery so the photo tracks the chosen colour.
  const images = useMemo(() => {
    const candidates = [...(variant?.images || []), product.heroImage, ...(product.images || [])];
    return [...new Set(candidates.filter(Boolean))];
  }, [product, variant]);

  // Mounted with key={variant.id} by the parent, so switching variant remounts
  // this component and the index resets to the primary shot for free — no effect
  // needed, and no chance of the index pointing past a shorter image list.
  const [activeIndex, setActiveIndex] = useState(0);

  const active = images[activeIndex] ?? images[0];

  return (
    <div>
      <ProductImage
        src={active}
        alt={`${product.name} — ${variant?.label ?? ''}`}
        priority
        className="aspect-square w-full rounded-card border border-ink-200/70 bg-white"
        imgClassName="object-contain p-6"
      />

      {images.length > 1 && (
        <div className="mt-3 flex gap-2.5" role="group" aria-label="Product images">
          {images.map((src, index) => (
            <button
              key={src}
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`View image ${index + 1} of ${images.length}`}
              aria-pressed={index === activeIndex}
              className={cx(
                'size-16 overflow-hidden rounded-xl border bg-white transition-all',
                index === activeIndex ? 'border-brand-500 ring-1 ring-brand-500' : 'border-ink-200 hover:border-brand-300',
              )}
            >
              <img src={src} alt="" className="size-full object-contain p-1.5" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Specs({ specs, highlights }) {
  if (!specs?.length && !highlights?.length) return null;

  return (
    <section className="mt-16 grid gap-10 lg:grid-cols-2" aria-labelledby="specs-heading">
      <div>
        <h2 id="specs-heading" className="text-xl font-bold tracking-tight text-ink-950">
          Specifications
        </h2>
        <dl className="mt-4 divide-y divide-ink-200/70 overflow-hidden rounded-card border border-ink-200/70 bg-white">
          {specs.map((spec) => (
            <div key={spec.label} className="grid grid-cols-3 gap-4 px-4 py-3 text-sm">
              <dt className="font-medium text-ink-500">{spec.label}</dt>
              <dd className="col-span-2 text-ink-900">{spec.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {highlights?.length > 0 && (
        <div>
          <h2 className="text-xl font-bold tracking-tight text-ink-950">Highlights</h2>
          <ul className="mt-4 space-y-3">
            {highlights.map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink-700">
                <span className="mt-1 grid size-4 shrink-0 place-items-center rounded-full bg-growth-100 text-growth-700" aria-hidden="true">
                  <svg viewBox="0 0 24 24" className="size-2.5" fill="none" stroke="currentColor" strokeWidth="4">
                    <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function DetailSkeleton() {
  return (
    <div className="container-page py-8" role="status" aria-label="Loading product">
      <Skeleton className="h-4 w-64" />
      <div className="mt-6 grid gap-10 lg:grid-cols-2 lg:gap-14">
        <Skeleton className="aspect-square w-full rounded-card" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-9 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();

  const { data, error, loading, reload } = useAsync((signal) => api.getProduct(slug, signal), [slug]);
  const product = data?.product;

  // Selection is stored together with the slug it belongs to. Navigating to a
  // different product invalidates it, and that is corrected during render rather
  // than in an effect — otherwise one frame would paint the new product with the
  // previous product's variant selected.
  const [selection, setSelection] = useState({ slug, variantId: null, tenure: null });

  if (selection.slug !== slug) {
    setSelection({ slug, variantId: null, tenure: null });
  }

  const { variantId: selectedVariantId, tenure: selectedTenure } = selection;
  const setSelectedVariantId = (variantId) => setSelection((prev) => ({ ...prev, variantId }));
  const setSelectedTenure = (tenure) => setSelection((prev) => ({ ...prev, tenure }));

  const [showSchedule, setShowSchedule] = useState(false);
  const [showProceed, setShowProceed] = useState(false);

  // Record the view once per product. The ref guards against StrictMode's
  // deliberate double-invocation in development inflating the count.
  const viewedSlug = useRef(null);
  useEffect(() => {
    if (!product || viewedSlug.current === product.slug) return;
    viewedSlug.current = product.slug;
    // Analytics must never break the page, so failures are swallowed.
    api.recordView(product.slug).catch(() => {});
  }, [product]);

  // Memoised so the fallback `[]` is not a fresh array on every render, which
  // would invalidate every downstream useMemo that depends on it.
  const variants = useMemo(() => product?.variants ?? [], [product]);

  // Default to the cheapest variant, which the API already returns first.
  const selectedVariant = useMemo(
    () => variants.find((v) => v.id === selectedVariantId) ?? variants[0] ?? null,
    [variants, selectedVariantId],
  );

  const plans = useMemo(() => selectedVariant?.emiPlans ?? [], [selectedVariant]);

  // Keep the shopper's tenure choice when they switch variant; fall back to the
  // plan flagged 'Most popular', then to the first plan available.
  const selectedPlan = useMemo(() => {
    if (!plans.length) return null;
    return (
      plans.find((p) => p.tenureMonths === selectedTenure) ??
      plans.find((p) => p.badge === 'Most popular') ??
      plans[0]
    );
  }, [plans, selectedTenure]);

  if (loading) return <DetailSkeleton />;

  if (error) {
    const notFound = error.status === 404;
    return (
      <div className="container-page py-20">
        <ErrorState
          title={notFound ? 'Product not found' : 'Could not load this product'}
          message={
            notFound
              ? `There is no product at /products/${slug}. It may have been removed or unpublished.`
              : error.message
          }
          onRetry={notFound ? undefined : reload}
        />
        <div className="mt-6 text-center">
          <Button to="/" variant="secondary">
            Back to all products
          </Button>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const savings = selectedVariant ? selectedVariant.mrp - selectedVariant.price : 0;

  return (
    <>
      <div className="container-page py-8">
        <Breadcrumbs product={product} />

        <div className="mt-6 grid gap-10 lg:grid-cols-2 lg:gap-14">
          {/* ── Left: imagery (sticky on desktop so it stays with the plans) ── */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            {/* Keyed on the variant so the thumbnail selection resets cleanly
                when the shopper switches colour or capacity. */}
            <Gallery key={selectedVariant?.id} product={product} variant={selectedVariant} />
          </div>

          {/* ── Right: purchase decision ─────────────────────────────────────── */}
          <div>
            {product.brand && (
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">{product.brand}</p>
            )}

            <h1 className="mt-1.5 text-3xl font-bold tracking-tight text-ink-950 sm:text-4xl">{product.name}</h1>

            {product.tagline && <p className="mt-2 text-base leading-relaxed text-ink-600">{product.tagline}</p>}

            <div className="mt-3 flex flex-wrap items-center gap-3">
              {product.rating ? <Rating value={product.rating} count={product.ratingCount} /> : null}
              {product.hasNoCostEmi && <Badge tone="growth">No cost EMI available</Badge>}
            </div>

            {/* Pricing */}
            {selectedVariant && (
              <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-3xl font-bold tracking-tight text-ink-950">
                  {currency(selectedVariant.price)}
                </span>
                {savings > 0 && (
                  <>
                    <span className="text-lg text-ink-400 line-through">{currency(selectedVariant.mrp)}</span>
                    <Badge tone="danger">
                      {percent(selectedVariant.discountPct, { decimals: 0 })} off — save {currency(savings)}
                    </Badge>
                  </>
                )}
              </div>
            )}

            {/* Variant selection */}
            <div className="mt-8">
              <VariantSelector
                variants={variants}
                selectedVariant={selectedVariant}
                onSelect={(variant) => setSelectedVariantId(variant.id)}
              />
            </div>

            {/* EMI plans */}
            <div className="mt-10">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-bold tracking-tight text-ink-950">Choose an EMI plan</h2>
                {selectedPlan && (
                  <button
                    type="button"
                    onClick={() => setShowSchedule(true)}
                    className="text-sm font-semibold text-brand-700 underline decoration-brand-300 underline-offset-2 hover:decoration-brand-600"
                  >
                    View repayment schedule
                  </button>
                )}
              </div>

              <p className="mt-1 text-sm text-ink-600">
                Backed by your pledged mutual funds. Longer tenures cost less per month but more overall.
              </p>

              <div className="mt-4">
                <EmiPlanSelector
                  plans={plans}
                  selectedPlan={selectedPlan}
                  onSelect={(plan) => setSelectedTenure(plan.tenureMonths)}
                />
              </div>
            </div>

            {/* Mutual fund collateral story */}
            {selectedPlan && (
              <div className="mt-8">
                <CollateralPanel plan={selectedPlan} />
              </div>
            )}

            {/* Commit */}
            <div className="mt-8">
              <Button
                size="lg"
                className="w-full"
                disabled={!selectedPlan || !selectedVariant?.inStock}
                onClick={() => setShowProceed(true)}
              >
                {selectedVariant?.inStock === false
                  ? 'Out of stock'
                  : selectedPlan
                    ? `Proceed with ${currency(selectedPlan.monthlyAmount)}/mo`
                    : 'Select a plan to continue'}
              </Button>

              <p className="mt-3 text-center text-xs text-ink-500">
                No payment is taken now. You will be asked to pledge units to complete the purchase.
              </p>
            </div>

            {product.description && (
              <div className="mt-10 border-t border-ink-200/70 pt-6">
                <h2 className="font-bold tracking-tight text-ink-950">About this phone</h2>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">{product.description}</p>
              </div>
            )}
          </div>
        </div>

        <Specs specs={product.specs} highlights={product.highlights} />
      </div>

      <ScheduleModal
        open={showSchedule}
        onClose={() => setShowSchedule(false)}
        slug={product.slug}
        variantId={selectedVariant?.id}
        plan={selectedPlan}
      />

      <ProceedModal
        open={showProceed}
        onClose={() => setShowProceed(false)}
        product={product}
        variant={selectedVariant}
        plan={selectedPlan}
      />
    </>
  );
}
